import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { dismissModal as dismissModalAction } from 'services/modal/redux/actions'
import isArray from 'lodash/isArray'
import { IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  AMBASSADOR_FORM_MODAL,
  CONFIRM_MODAL,
  ERRORS_MODAL,
  FETCHING_MODAL,
  NEW_CAMPAIGN_FORM_MODAL,
  EDIT_CAMPAIGN_FORM_MODAL,
  REDEEM_GIFT_MODAL,
  ADD_PARTNER_FORM_MODAL,
  REMOVE_PARTNER_FORM_MODAL,
} from 'services/modal'
import AmbassadorFormModal from '../ambassadors/AmbassadorFormModal'
import ConfirmModal from '../confirm-modal'
import ErrorsModal from '../errors-modal'
import { RedeemGiftModal } from '../RedeemGiftModal'
import { FetchingModal } from '../FetchingModal'
import {
  AddPartnerModal,
  EditOrCreateCampaignModal,
  RemovePartnerModal,
} from '../partner-dashboard/CampaignModals'

import './index.scss'

/**
 * Render the corresponding component based on name & props.
 * @param {string} componentName
 * @param {object} props
 */
const getComponent = (componentName, props) => {
  const modals = {
    [AMBASSADOR_FORM_MODAL]: AmbassadorFormModal,
    [NEW_CAMPAIGN_FORM_MODAL]: EditOrCreateCampaignModal,
    [EDIT_CAMPAIGN_FORM_MODAL]: EditOrCreateCampaignModal,
    [ADD_PARTNER_FORM_MODAL]: AddPartnerModal,
    [REMOVE_PARTNER_FORM_MODAL]: RemovePartnerModal,
    [CONFIRM_MODAL]: ConfirmModal,
    [ERRORS_MODAL]: ErrorsModal,
    [FETCHING_MODAL]: FetchingModal,
    [REDEEM_GIFT_MODAL]: RedeemGiftModal,
  }
  const Component = modals[componentName] || null
  if (!Component) {
    return null
  }
  return <Component {...props} />
}

/**
 * Get the corresponding classes to the different elements of the modal.
 * @param {string} base
 * @param {string} otherClass
 */
const getClassName = (base, otherClass) => {
  const cls = [base]
  if (otherClass) {
    const classes = isArray(otherClass) ? otherClass : [otherClass]
    cls.push(...classes)
  }
  return cls.join(' ')
}

/**
 * Get the corresponding classes for the wrapper.
 * @param {string} innerClass
 * @param {boolean} fitHeight
 * @param {boolean} fitWidth
 */
const getModalInnerClassName = (innerClass, fitHeight, fitWidth) => {
  const base = getClassName('modal__inner', innerClass)
  const extraClasses = []
  if (fitHeight) extraClasses.push('modal__inner--fit-height')
  if (fitWidth) extraClasses.push('modal__inner--fit-width')
  return getClassName(base, extraClasses)
}

/**
 * Adds/removes a custom class to the body element to identify
 * if the modal is currently open or closed.
 * @param {boolean} add
 */
const applyBodyClass = (add) => {
  const { document = {} } = global
  const { body } = document
  if (body && body.classList) {
    if (add) body.classList.add('body__modal--open')
    else body.classList.remove('body__modal--open')
  }
}

const Modal = (props) => {
  const { modal, modalProps, dismissModal } = props
  const {
    backgroundDismiss = true,
    bodyInnerClass,
    overlayClass,
    hideDismiss,
    innerClass,
    onDismiss,
    bodyClass,
    fitHeight,
    fitWidth,
  } = modalProps
  const isOpen = modal.get('isOpen')

  useEffect(() => {
    applyBodyClass(true)

    return () => {
      applyBodyClass(false)
    }
  }, [])

  const onDismissHandler = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (onDismiss) {
      onDismiss(e)
    }
    dismissModal()
  }

  const stopPropagation = (e) => {
    e.stopPropagation()
  }

  return ReactDOM.createPortal(
    !isOpen ? null : (
      <section
        role="dialog"
        aria-modal="true"
        onClick={backgroundDismiss ? onDismissHandler : null}
        className={getClassName('modal', overlayClass)}
      >
        <div
          className={getModalInnerClassName(innerClass, fitHeight, fitWidth)}
          onClick={stopPropagation}
        >
          <div className={getClassName('modal__body', bodyClass)}>
            <div className={getClassName('modal__body-inner', bodyInnerClass)}>
              {getComponent(modal.get('componentName'), modalProps)}
            </div>
          </div>
          {hideDismiss ? null : (
            <IconButton
              onClick={onDismissHandler}
              className="modal__close"
              sx={{
                position: 'absolute',
                right: '16px',
                top: '16px',
                color: '#1976d2',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </div>
      </section>
    ),
    document.body,
  )
}

Modal.propTypes = {
  modal: ImmutablePropTypes.map.isRequired,
  modalProps: PropTypes.object.isRequired,
  dismissModal: PropTypes.func.isRequired,
  fitHeight: PropTypes.bool,
  fitWidth: PropTypes.bool,
}

Modal.defaultProps = {
  fitHeight: false,
  fitWidth: false,
}

export default connect(
  (state) => ({
    modal: state.modal,
    modalProps: state.modal.get('props', {}),
  }),
  (dispatch) =>
    bindActionCreators(
      {
        dismissModal: dismissModalAction,
      },
      dispatch,
    ),
)(Modal)
